/**
 * Utilities for managing calendar view navigation state in localStorage
 */

const LAST_CALENDAR_VIEW_KEY = "last-calendar-view";
const LAST_FLOW_WORKSPACE_VIEW_KEY = "last-flow-workspace-view";
const LAST_CALENDAR_FOCUS_DATE_KEY = "last-calendar-focus-date";

export type CalendarViewType = "calendar" | "flowboard";
export type FlowWorkspaceView = "flowboard" | "grooming";

/**
 * Saves the last visited calendar view to localStorage
 * @param view - The view type to save ("calendar" or "flowboard")
 */
export function saveLastCalendarView(view: CalendarViewType): void {
  try {
    localStorage.setItem(LAST_CALENDAR_VIEW_KEY, view);
  } catch (error) {
    // Ignore localStorage errors (e.g., blocked, quota exceeded)
    console.warn("Failed to save last calendar view:", error);
  }
}

/**
 * Retrieves the last visited calendar view from localStorage
 * @returns The last view type, or "calendar" as default if not found or invalid
 */
export function getLastCalendarView(): CalendarViewType {
  try {
    const saved = localStorage.getItem(LAST_CALENDAR_VIEW_KEY);
    if (saved === "calendar" || saved === "flowboard") {
      return saved;
    }
  } catch (error) {
    // Ignore localStorage errors
    console.warn("Failed to read last calendar view:", error);
  }
  return "calendar"; // Default to calendar view
}

/**
 * Saves the last visited flow workspace view to localStorage
 * @param view - The workspace view type to save ("flowboard" or "grooming")
 */
export function saveLastFlowWorkspaceView(view: FlowWorkspaceView): void {
  try {
    localStorage.setItem(LAST_FLOW_WORKSPACE_VIEW_KEY, view);
  } catch (error) {
    // Ignore localStorage errors (e.g., blocked, quota exceeded)
    console.warn("Failed to save last flow workspace view:", error);
  }
}

/**
 * Retrieves the last visited flow workspace view from localStorage
 * @returns The last workspace view type, or "flowboard" as default if not found or invalid
 */
export function getLastFlowWorkspaceView(): FlowWorkspaceView {
  try {
    const saved = localStorage.getItem(LAST_FLOW_WORKSPACE_VIEW_KEY);
    if (saved === "flowboard" || saved === "grooming") {
      return saved;
    }
  } catch (error) {
    // Ignore localStorage errors
    console.warn("Failed to read last flow workspace view:", error);
  }

  return "flowboard";
}

/**
 * Resolves the last flow workspace path while respecting grooming access.
 */
export function getLastFlowWorkspacePath(
  canAccessGrooming: boolean,
): "/calendar/flowboard" | "/grooming" {
  const lastView = getLastFlowWorkspaceView();

  if (lastView === "grooming" && canAccessGrooming) {
    return "/grooming";
  }

  return "/calendar/flowboard";
}

/**
 * Saves the last date the user was focusing in calendar-related views.
 */
export function saveLastCalendarFocusDate(date: Date): void {
  try {
    localStorage.setItem(LAST_CALENDAR_FOCUS_DATE_KEY, date.toISOString());
  } catch (error) {
    console.warn("Failed to save last calendar focus date:", error);
  }
}

/**
 * Retrieves the last date the user was focusing in calendar-related views.
 */
export function getLastCalendarFocusDate(): Date | null {
  try {
    const saved = localStorage.getItem(LAST_CALENDAR_FOCUS_DATE_KEY);
    if (!saved) return null;

    const parsed = new Date(saved);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  } catch (error) {
    console.warn("Failed to read last calendar focus date:", error);
  }

  return null;
}
